import clsx from 'clsx';
import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Props {
  operators: string[];
  selected: Set<string>;
  // Operators to hide from the picker entirely (e.g. ground-truth operators,
  // which would self-grade if they were participants).
  excludedOperators?: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  // Optional override for the closed-state button label. Useful when the
  // picker is being reused outside the original "participants" context (e.g.
  // for selecting ground-truth operators).
  buttonLabel?: (selectedCount: number) => string;
  // Optional override for the modal title.
  title?: string;
  // Optional override for the empty-state message inside the picker.
  emptyMessage?: string;
}

export function ParticipantPicker({
  operators,
  selected,
  excludedOperators,
  onChange,
  buttonLabel,
  title,
  emptyMessage,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      operators.filter(
        (op) =>
          !(excludedOperators?.has(op) ?? false) && op.toLowerCase().includes(search.toLowerCase())
      ),
    [operators, excludedOperators, search]
  );

  const allFilteredSelected = filtered.length > 0 && filtered.every((op) => selected.has(op));

  const toggleOne = (op: string) => {
    const next = new Set(selected);
    if (next.has(op)) next.delete(op);
    else next.add(op);
    onChange(next);
  };

  const selectAllFiltered = () => {
    const next = new Set(selected);
    for (const op of filtered) next.add(op);
    onChange(next);
  };

  const clearAllFiltered = () => {
    const next = new Set(selected);
    for (const op of filtered) next.delete(op);
    onChange(next);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={clsx(
          'rounded-lg border px-4 py-2 text-xs font-medium transition',
          selected.size > 0
            ? 'border-brand-orange bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/20'
            : 'border-brand-border bg-brand-dark text-brand-text-secondary hover:text-white hover:border-brand-text-secondary/50'
        )}
      >
        {buttonLabel
          ? buttonLabel(selected.size)
          : selected.size === 0
            ? 'Pick participants…'
            : `${selected.size} participant${selected.size !== 1 ? 's' : ''} selected`}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Close"
            onClick={() => setOpen(false)}
          />
          <div className="relative flex flex-col bg-brand-surface border border-brand-border rounded-xl w-full max-w-sm max-h-[75vh] shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-brand-border shrink-0">
              <span className="text-sm font-semibold text-brand-text-primary">
                {title ?? 'Participants'}
                {selected.size > 0 && (
                  <span className="ml-2 text-brand-orange">{selected.size} selected</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-brand-text-secondary hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 py-3 border-b border-brand-border shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-text-secondary pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search prefix, e.g. LN…"
                  className="w-full h-8 rounded-lg border border-brand-border bg-brand-dark pl-8 pr-3 text-xs text-brand-text-primary placeholder-brand-text-secondary focus:border-brand-orange focus:outline-none"
                />
              </div>
            </div>

            {/* Select-all / clear row */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-brand-border bg-brand-surface-highlight/30 shrink-0">
              <span className="text-xs text-brand-text-secondary">{filtered.length} shown</span>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  disabled={allFilteredSelected || filtered.length === 0}
                  className="text-xs text-brand-orange hover:underline disabled:opacity-40 disabled:no-underline"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={clearAllFiltered}
                  disabled={filtered.every((op) => !selected.has(op))}
                  className="text-xs text-brand-text-secondary hover:text-white hover:underline disabled:opacity-40 disabled:no-underline"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <div className="py-8 text-center text-xs text-brand-text-secondary">
                  {search ? 'No operators match.' : (emptyMessage ?? 'No operators available.')}
                </div>
              ) : (
                filtered.map((op) => {
                  const checked = selected.has(op);
                  return (
                    <button
                      key={op}
                      type="button"
                      onClick={() => toggleOne(op)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-xs text-left transition hover:bg-brand-surface-highlight/40',
                        checked ? 'text-brand-text-primary' : 'text-brand-text-secondary'
                      )}
                    >
                      <span
                        className={clsx(
                          'shrink-0 h-4 w-4 rounded border flex items-center justify-center',
                          checked
                            ? 'bg-brand-orange border-brand-orange'
                            : 'bg-transparent border-brand-border'
                        )}
                      >
                        {checked && (
                          <svg
                            viewBox="0 0 10 8"
                            className="h-2.5 w-2.5 fill-white"
                            aria-hidden="true"
                          >
                            <path
                              d="M1 4l3 3 5-6"
                              stroke="white"
                              strokeWidth="1.5"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      <span className="truncate font-mono">{op}</span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-brand-border shrink-0 flex justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg bg-brand-orange px-5 py-2 text-xs font-bold text-white hover:bg-brand-orange-hover"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
